import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { API_BASE_URL } from '@/config';
import VerificationLoader from '@/components/LoadingAnimation/VerificationLoader';

interface MobileOTPLoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onNext?: () => void;  // Callback to advance journey after successful login
  requestOtpEndpoint?: string;  // Optional: Override default OTP request endpoint
  verifyOtpEndpoint?: string;   // Optional: Override default OTP verify endpoint
}

/**
 * MobileOTPLogin - Combined component for mobile number input and OTP verification
 * 
 * This component handles the complete OTP login flow:
 * 1. Mobile number input with validation
 * 2. Request OTP button
 * 3. OTP input field (shown after OTP is requested)
 * 4. Verify OTP button
 * 
 * @component
 */
const MobileOTPLogin: React.FC<MobileOTPLoginProps> = ({
  onSuccess,
  onError,
  onNext,
  requestOtpEndpoint = '/api/v3/onboarding/request-otp/',
  verifyOtpEndpoint = '/api/v3/onboarding/verify-otp/',
}) => {
  const [mobile, setMobile] = useState('');
  const [otp, setOTP] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [showVerificationLoader, setShowVerificationLoader] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  const validateMobileNumber = (mobileNum: string): boolean => {
    if (!mobileNum) {
      setMobileError('Mobile number is required');
      return false;
    }

    const cleaned = mobileNum.replace(/[\s\-\+]/g, '');

    if (!/^\d+$/.test(cleaned)) {
      setMobileError('Mobile number must contain only digits');
      return false;
    }

    if (!/^[6-9]/.test(cleaned)) {
      setMobileError('Invalid mobile number');
      return false;
    }

    if (cleaned.length !== 10) {
      setMobileError('Mobile number must be 10 digits');
      return false;
    }

    setMobileError('');
    return true;
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(value);

    // Live feedback: bad starting digit → invalid; correct start but incomplete → 10-digit hint
    if (!value) {
      setMobileError('');
    } else if (!/^[6-9]/.test(value)) {
      setMobileError('Invalid mobile number');
    } else if (value.length !== 10) {
      setMobileError('Mobile number must be 10 digits');
    } else {
      setMobileError('');
    }
  };

  const handleOTPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOTP(value);
  };

  const requestOTP = async () => {
    if (!validateMobileNumber(mobile)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Extract source parameter from URL query string
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source') || '';

      // Use configurable endpoint (defaults to v3 API with DigiTap integration)
      // This respects the BAP set in session via set_bap_identifier_cookie
      // Note: Don't use API_BASE_URL - Vite proxy handles /api/* automatically
      const response = await axios.post(requestOtpEndpoint, {
        mobile_number: mobile,
        source: source,  // Send source slug for journey resolution
      });

      setOtpRequested(true);
      setError('');
    } catch (err: any) {
      const errorMsg = err.response?.data || err.message || 'Failed to send OTP. Please try again.';
      setError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');
    setShowVerificationLoader(true); // Show beautiful loading animation

    try {
      // Use configurable endpoint (defaults to v3 API with DigiTap integration)
      // This respects the BAP set in session
      // Note: Don't use API_BASE_URL - Vite proxy handles /api/* automatically
      const response = await axios.post(verifyOtpEndpoint, {
        otp: otp,
        accepts_terms_and_privacy: true,
      });

      // Check if this is a new user (for DigiTap enrichment tracking)
      if (response.data?.is_new_user) {
        setIsNewUser(true);
        console.log('[MobileOTPLogin] New user created with DigiTap enrichment:', response.data.digitap_enrichment);
      }

      // Keep loader visible for smooth transition
      // The loader will automatically progress through its steps
      
      // Success - call onNext to advance journey to next page
      // This allows JourneyPage to handle the navigation properly
      if (onNext) {
        onNext();
      } else {
        // Fallback: reload page if onNext not provided (backward compatibility)
        window.location.reload();
      }
      
      // Also call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setShowVerificationLoader(false); // Hide loader on error
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.response?.data || 'Invalid OTP. Please try again.';
      setError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
    } finally {
      setLoading(false);
      // Note: We don't hide the loader here on success - it stays visible during navigation
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!otpRequested) {
        requestOTP();
      } else {
        verifyOTP();
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Mobile Number Input */}
      <div className="space-y-2">
        <Label htmlFor="mobile" className="text-sm font-medium text-gray-900">
          Mobile Number <span className="text-red-500">*</span>
        </Label>
        <div className="relative">
          {/* Country code prefix — always visible, before and after OTP request.
              z-10 is required: the shadcn Input renders its <input> inside its own
              `relative` wrapper, which would otherwise paint over this span. */}
          <div className="absolute inset-y-0 left-0 z-10 pl-3 pr-2 flex items-center pointer-events-none">
            <span className="text-base font-medium text-gray-900">+91</span>
          </div>
          <Input
            id="mobile"
            type="tel"
            value={mobile}
            onChange={handleMobileChange}
            onKeyPress={handleKeyPress}
            disabled={otpRequested || loading}
            placeholder="9876543210"
            maxLength={10}
            className={`pl-16 text-base ${mobileError ? 'border-red-300' : 'border-intellend-green'} ${otpRequested ? 'bg-gray-50' : ''}`}
            autoFocus
          />
        </div>
        {mobileError && (
          <p className="text-sm text-red-600">{mobileError}</p>
        )}
      </div>

      {/* OTP Input - Only shown after OTP is requested */}
      {otpRequested && (
        <div className="space-y-2 animate-fade-in">
          <Label htmlFor="otp" className="text-sm font-medium text-gray-900">
            Enter OTP <span className="text-red-500">*</span>
          </Label>
          <Input
            id="otp"
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={handleOTPChange}
            onKeyPress={handleKeyPress}
            disabled={loading}
            placeholder="Enter 6-digit OTP"
            maxLength={6}
            className="text-center text-lg tracking-widest font-semibold"
            autoFocus
          />
          <p className="text-xs text-gray-500 text-center">
            {otp.length}/6 digits entered
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {!otpRequested ? (
          <Button
            onClick={requestOTP}
            disabled={loading || !mobile || mobile.length !== 10}
            className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white py-6 text-base font-semibold"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending OTP...
              </span>
            ) : 'Request OTP'}
          </Button>
        ) : (
          <>
            <Button
              onClick={verifyOTP}
              disabled={loading || !otp || otp.length !== 6}
              className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white py-6 text-base font-semibold"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : 'Verify OTP & Login'}
            </Button>
            <Button
              onClick={() => {
                setOtpRequested(false);
                setOTP('');
                setError('');
              }}
              variant="outline"
              disabled={loading}
              className="w-full py-6 text-base"
            >
              Change Mobile Number
            </Button>
          </>
        )}
      </div>

      {/* OTP Sent Message */}
      {otpRequested && !error && (
        <Alert className="bg-intellend-green/5 border-intellend-green/20">
          <AlertDescription className="text-intellend-green">
            OTP sent to +91 {mobile}. Please check your SMS.
          </AlertDescription>
        </Alert>
      )}

      {/* Verification Loader - Shows during OTP verification */}
      {showVerificationLoader && <VerificationLoader isNewUser={isNewUser} />}
    </div>
  );
};

export default MobileOTPLogin;
